import { useMemo, useState } from 'react';
import { Table, Input, Switch, Alert, Space, Tag, message } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import {
    ContentCard,
    useUsers,
    useBoards,
    UserLogModal,
    UserEditModal,
    type UserData,
} from '../../component/admin';
import { useUserBoardPermissions } from '../../component/admin/hooks/useUserBoardPermissions';
import { getUserPermissionColumns } from '../../component/admin/config/columns/userPermissionColumns';
import UserBoardPermissionModal from '../../component/admin/UserBoardPermissionModal';
import UserPermissionDetail from '../../component/admin/UserPermissionDetail';
import {
    editUserName,
    editUserPassword,
    withdrawUser,
    changeUserRole,
} from '../../api/adminAPI';

const isAdminRole = (role: string) => role === 'ADMIN_MAIN' || role === 'ADMIN_SUB';

const PermissionManagePage = () => {
    const { users, loading, error, refetch, setSearchKeyword } = useUsers();
    const { boards, loading: boardsLoading } = useBoards();
    const {
        permBoards,
        statusByUser,
        categoriesByBoard,
        loading: permLoading,
        refetch: refetchPerms,
    } = useUserBoardPermissions(boards);

    const [showPermissions, setShowPermissions] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);

    // 조회 이력 모달
    const [isLogModalOpen, setIsLogModalOpen] = useState(false);
    const [logTargetUser, setLogTargetUser] = useState<UserData | null>(null);

    // 권한 부여 모달
    const [isGrantModalOpen, setIsGrantModalOpen] = useState(false);
    const [grantTargetUser, setGrantTargetUser] = useState<UserData | null>(null);

    // 유저 정보 수정 모달
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editTargetUser, setEditTargetUser] = useState<UserData | null>(null);

    const handleViewLogs = (user: UserData) => {
        setLogTargetUser(user);
        setIsLogModalOpen(true);
    };

    const handleGrant = (user: UserData) => {
        setGrantTargetUser(user);
        setIsGrantModalOpen(true);
    };

    const handleEdit = (user: UserData) => {
        setEditTargetUser(user);
        setIsEditModalOpen(true);
    };

    const handleEditModalClose = () => {
        setIsEditModalOpen(false);
        setEditTargetUser(null);
    };

    const handleEditSubmit = async (data: any) => {
        try {
            const [, roleResult] = await Promise.all([
                editUserName(data.userId, data.name),
                changeUserRole({
                    userId: data.userId,
                    englishName: data.englishName,
                    role: data.role,
                    email: data.email,
                    birthDate: data.birthDate,
                    phone: data.phone,
                    workplace: data.workplace,
                    medicalLicenseNumber: data.medicalLicenseNumber,
                }),
            ]);

            if (!roleResult.success) {
                message.error(roleResult.message);
                return;
            }

            message.success('유저 정보가 성공적으로 변경되었습니다.');
            refetch();
            handleEditModalClose();
        } catch (e) {
            console.log(e);
            message.error('유저 정보 변경에 실패했습니다.');
        }
    };

    const handlePasswordChange = (userId: number, newPassword: string) => {
        editUserPassword(userId, newPassword)
            .then(() => message.success('비밀번호가 성공적으로 변경되었습니다.'))
            .catch((e) => {
                console.log(e);
                message.error('비밀번호 변경에 실패했습니다.');
            });
    };

    const handleDeleteUser = async (userId: number) => {
        try {
            await withdrawUser(userId);
            message.success('유저가 성공적으로 탈퇴 처리되었습니다.');
            refetch();
            handleEditModalClose();
        } catch (e) {
            message.error('유저 탈퇴 처리에 실패했습니다.');
        }
    };

    const columns = useMemo(
        () =>
            getUserPermissionColumns({
                showPermissions,
                permBoards,
                statusByUser,
                permLoading,
                onGrant: handleGrant,
                onViewLogs: handleViewLogs,
                onEdit: handleEdit,
            }),
        [showPermissions, permBoards, statusByUser, permLoading],
    );

    const activeUsers = useMemo(
        () => users?.filter((user) => !user.isWithdrawnYN) || [],
        [users],
    );

    // 권한이 하나라도 있는 유저만 행을 펼칠 수 있게 (빈 상세 노출 방지)
    const hasAnyPermission = (userId: number) => {
        const status = statusByUser[userId];
        return status ? Object.values(status).some((v) => v !== 'NONE') : false;
    };

    if (loading) return <div>loading...</div>;
    if (error) return <div>error: {error}</div>;

    return (
        <>
            <ContentCard title="유저 권한 관리">
                <AdminInfoBox>
                    <Alert
                        message="유저 목록에서 바로 게시판-카테고리 권한을 확인하고 부여할 수 있습니다."
                        description={
                            <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                                <li>
                                    <strong>게시판 권한 보기</strong> 스위치를 켜면 면허번호·전문의번호·휴대폰·가입일·활동
                                    컬럼이 각 유저의 <strong>게시판-카테고리 권한 목록</strong>으로 바뀝니다.
                                </li>
                                <li>
                                    행을 펼치면 게시판별로 권한이 부여된 <strong>카테고리 이름</strong>까지 확인할 수 있습니다.
                                </li>
                                <li>
                                    <strong>권한 부여</strong> 버튼으로 해당 유저에게 게시판별 카테고리 권한을 바로 부여/회수할 수 있습니다.
                                </li>
                                <li style={{ color: '#8c8c8c' }}>
                                    <Tag color="blue">전체</Tag> 게시판의 모든 카테고리 권한 /{' '}
                                    <Tag color="green">부분</Tag> 일부 카테고리 권한
                                </li>
                            </ul>
                        }
                        type="info"
                        showIcon
                        icon={<InfoCircleOutlined />}
                        closable
                    />
                </AdminInfoBox>

                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 16,
                        gap: 12,
                        flexWrap: 'wrap',
                    }}
                >
                    <Input.Search
                        placeholder="ID, 이름, 이메일, 소속, 휴대폰 검색"
                        allowClear
                        onSearch={(value) => {
                            setSearchKeyword(value);
                            setCurrentPage(1);
                        }}
                        onChange={(e) => setSearchKeyword(e.target.value)}
                        style={{ width: 320 }}
                    />

                    <Space>
                        <span style={{ color: '#595959' }}>게시판 권한 보기</span>
                        <Switch
                            checked={showPermissions}
                            onChange={setShowPermissions}
                            checkedChildren="ON"
                            unCheckedChildren="OFF"
                        />
                    </Space>
                </div>

                <StyledTable
                    size="small"
                    rowKey="userId"
                    columns={columns}
                    dataSource={activeUsers}
                    loading={boardsLoading}
                    scroll={{ x: 'max-content' }}
                    expandable={
                        showPermissions
                            ? {
                                  rowExpandable: (record) =>
                                      !isAdminRole(record.role) && hasAnyPermission(record.userId),
                                  expandedRowRender: (record) => (
                                      <UserPermissionDetail
                                          userId={record.userId}
                                          permBoards={permBoards}
                                          statusByBoard={statusByUser[record.userId] || {}}
                                          categoriesByBoard={categoriesByBoard}
                                      />
                                  ),
                              }
                            : undefined
                    }
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '15', '20', '25'],
                        showTotal: (total) => `Total ${total}`,
                        onChange: (page, size) => {
                            setCurrentPage(page);
                            setPageSize(size);
                        },
                    }}
                />
            </ContentCard>

            <UserLogModal
                open={isLogModalOpen}
                user={logTargetUser}
                onClose={() => setIsLogModalOpen(false)}
            />

            <UserEditModal
                open={isEditModalOpen}
                user={editTargetUser}
                onClose={handleEditModalClose}
                onSubmit={handleEditSubmit}
                onPasswordChange={handlePasswordChange}
                onDelete={handleDeleteUser}
            />

            <UserBoardPermissionModal
                open={isGrantModalOpen}
                user={grantTargetUser}
                permBoards={permBoards}
                categoriesByBoard={categoriesByBoard}
                onClose={() => {
                    setIsGrantModalOpen(false);
                    setGrantTargetUser(null);
                }}
                onSaved={() => {
                    refetchPerms();
                    refetch();
                }}
            />
        </>
    );
};

const AdminInfoBox = styled.div`
    margin-bottom: 24px;

    .ant-alert-info {
        background-color: #f0f7ff;
        border: 1px solid #adc6ff;
        border-radius: 8px;
    }

    .ant-alert-message {
        font-weight: bold;
        color: #1d39c4;
    }

    .ant-alert-description {
        color: #262626;
        font-size: 14px;
    }
`;

const StyledTable = styled(Table<UserData>)`
    .ant-table-cell {
        vertical-align: middle;
    }
`;

export default PermissionManagePage;
